import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { VisitRequestTableRow, VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { BulkVisitRequestsReadyForPromotionQueueTableQuery } from '@/components/bulkQueue/tables/readyForPromotion/BulkReadyForPromotionQueueTable.gql.types';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { AxleChip } from '@/components/displayFields/chips';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { HorizontalStack } from '@/components/utils/linearLayouts';
import { usePromoteMultipleVisitRequests } from '@/components/visitRequests/mutations/promoteVisitRequest';
import { VisitColumns } from '@/components/visits/tables/columns';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { useVisitRequestListPageContext } from '@/pages/VisitRequestsListPage/VisitRequestsListPageContext';
import { gql } from '@apollo/client';
import { ScheduleSend } from '@mui/icons-material';
import { Button, Checkbox, CircularProgress, Typography } from '@mui/material';
import { ComponentProps, FC, useCallback, useEffect, useMemo } from 'react';

export const BULK_READY_FOR_PROMOTION_QUEUE_TABLE_QUERY = gql`
    query BulkVisitRequestsReadyForPromotionQueueTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
        $visitsIncluded: Boolean!
        $visitRequestFilters: VisitRequestFilter
        $visitRequestPagination: PaginationInput
        $visitRequestOrdering: VisitRequestOrdering
        $visitRequestsIncluded: Boolean!
    ) {
        visitRequests(
            filters: $visitRequestFilters
            pagination: $visitRequestPagination
            ordering: $visitRequestOrdering
        ) @include(if: $visitRequestsIncluded) {
            totalCount
            items {
                ...VisitRequestTableRow
            }
        }
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering)
            @include(if: $visitsIncluded) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitTableRow}
    ${VisitRequestTableRow}
`;

type BulkReadyForPromotionQueueTableProps = {
    title?: string;
    rows: BulkTableRow[];
    pagination: Pagination;
    serverSideSort: ServerSideSort;
};

export const BulkReadyForPromotionQueueTable: FC<BulkReadyForPromotionQueueTableProps> = (
    props,
) => {
    const { rows, pagination, title, serverSideSort } = props;

    const {
        selectedIds: [selectedVisitRequestIds, setSelectedVisitRequestIds],
    } = useVisitRequestListPageContext();

    const { promoteMultipleVisitRequests, loading: bulkPromotionInFlight } =
        usePromoteMultipleVisitRequests();

    const tableComponents = useMemo(
        () => ({
            Checkbox: (checkboxProps: ComponentProps<typeof Checkbox>) => (
                <Checkbox {...checkboxProps} disabled={bulkPromotionInFlight} />
            ),
        }),
        [bulkPromotionInFlight],
    );

    async function handlePromoteVisitRequests() {
        await promoteMultipleVisitRequests({
            visitRequestIds: selectedVisitRequestIds,
        });
        setSelectedVisitRequestIds([]);
    }

    const columns: RawColumn<BulkTableRow>[] = useMemo(
        () => [
            {
                field: 'sortPriority',
                label: 'Priority',
                rowToComponent: (entity) => <PriorityRenderCell entity={entity} />,
                options: { ...VisitColumns.priorityColumnOptions, sort: false },
            },
            {
                field: 'patient.fullName',
                label: 'Patient',
                rowToComponent: (entity) => <PatientRenderCell entity={entity} />,
            },
            {
                field: 'sortServicesName',
                label: 'Services',
                rowToComponent: (entity) => <ServicesRenderCell entity={entity} />,
                options: { sort: false },
            },
            {
                field: 'cliniciansSortKey',
                label: 'Clinician',
                rowToComponent: (entity) => <ClinicianRenderCell entity={entity} />,
                options: { sort: false },
            },
            {
                field: 'sortDate',
                label: 'Date',
                rowToComponent: (entity) => <DateRenderCell entity={entity} />,
            },
            {
                field: 'fulfillmentWindow.startDate',
                label: 'Fulfillment Window',
                rowToComponent: (entity) => <FulfillmentWindowRenderCell entity={entity} />,
            },
            {
                field: 'orderSortKey',
                label: 'Order',
                rowToComponent: (entity) => <OrderRenderCell entity={entity} />,
                options: { sort: false },
            },
            {
                field: '',
                label: 'Actions',
                rowToComponent: (entity) => <ActionsRenderCell entity={entity} />,
            },
        ],
        [],
    );

    return (
        <AxleTable
            toolbar={false}
            title={
                typeof title === 'string' ? (
                    <HorizontalStack spacing={4}>
                        <div>
                            <Typography variant='h2'>{title}</Typography>
                        </div>
                        <HorizontalStack spacing={2} width='auto'>
                            <Button
                                startIcon={<ScheduleSend />}
                                variant='contained'
                                color='primary'
                                disableElevation
                                disabled={
                                    selectedVisitRequestIds.length === 0 || bulkPromotionInFlight
                                }
                                onClick={() => {
                                    void handlePromoteVisitRequests();
                                }}
                            >
                                Send to Scheduling
                            </Button>
                            <AxleChip
                                variant='outlined'
                                label={`${selectedVisitRequestIds.length}`}
                                sx={{ fontWeight: 'normal' }}
                            />
                            {bulkPromotionInFlight && <CircularProgress size={20} />}
                        </HorizontalStack>
                    </HorizontalStack>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            rowToLink={rowToLink}
            columns={columns}
            serverSideSort={serverSideSort}
            selectableRows='multiple'
            rowToSelectionIdentifier={(row) => row.id}
            selectedRowValues={selectedVisitRequestIds}
            setSelectedRowValues={setSelectedVisitRequestIds}
            components={tableComponents}
        />
    );
};

type ConnectedBulkReadyForPromotionQueueTableProps = {
    title?: string;
    onData?(data: BulkTableRow[]): void;
};

export const ConnectedBulkReadyForPromotionQueueTable: FC<
    ConnectedBulkReadyForPromotionQueueTableProps
> = (props) => {
    const { onData, title } = props;
    const { rows, pagination, serverSideSort, showSkeleton, data } =
        useConnectedBulkTable<BulkVisitRequestsReadyForPromotionQueueTableQuery>({
            query: BULK_READY_FOR_PROMOTION_QUEUE_TABLE_QUERY,
        });

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    useEffect(() => {
        if (typeof onData === 'function') {
            onData(data?.visitRequests?.items ?? []);
        }
    }, [onData, data]);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkReadyForPromotionQueueTable
            rows={rows}
            title={title}
            pagination={pagination}
            serverSideSort={serverSideSort}
        />
    );
};
